import { createApi } from "@reduxjs/toolkit/query/react";

import {
  baseQuery,
  GET_FIRST_SALE_SUPPLIERS,
  GET_FIRST_SALE_SUPPLIER,
  GET_FIRST_SALE_CASES,
  GET_FIRST_SALE_CASE,
  GET_FIRST_SALE_DOCUMENTS,
  GET_FIRST_SALE_DOCUMENT_URL,
  GET_FIRST_SALE_VALIDATION,
  GET_FIRST_SALE_DOCUMENT_SECTIONS,
  CREATE_FIRST_SALE_SUPPLIER,
  UPDATE_FIRST_SALE_SUPPLIER,
  CREATE_FIRST_SALE_CASE,
  UPDATE_FIRST_SALE_CASE,
  UPLOAD_FIRST_SALE_DOCUMENT,
  DELETE_FIRST_SALE_DOCUMENT,
  DELETE_FIRST_SALE_CASE,
  DELETE_FIRST_SALE_SUPPLIER,
  RUN_FIRST_SALE_VALIDATION,
  RERUN_FIRST_SALE_VALIDATION_CHECK,
  UPDATE_VALIDATION_CHECK_REVIEW,
  SIGN_OFF_VALIDATION,
  GET_FIRST_SALE_ACTIVITY_EVENTS,
  CREATE_FIRST_SALE_ACTIVITY_EVENT,
  GET_FIRST_SALE_CASE_QUESTIONS,
  GET_FIRST_SALE_DOCUMENT_REQUESTS,
  CREATE_FIRST_SALE_DOCUMENT_REQUESTS,
  FULFILL_FIRST_SALE_DOCUMENT_REQUEST,
  UPDATE_FIRST_SALE_DOCUMENT_REQUEST,
  DELETE_FIRST_SALE_DOCUMENT_REQUEST,
  CONFIRM_FIRST_SALE_DOCUMENT_UPLOAD,
  GENERATE_FIRST_SALE_MEMO,
  GENERATE_FIRST_SALE_NOTIFICATION_LETTER,
  GET_FIRST_SALE_GENERATED_DOCS,
  SAVE_FIRST_SALE_GENERATED_DOC,
  UPDATE_FIRST_SALE_GENERATED_DOC,
  DELETE_FIRST_SALE_GENERATED_DOC,
  GET_FIRST_SALE_DATA_EXTRACTION,
  RUN_FIRST_SALE_DATA_EXTRACTION,
  UPDATE_DATA_EXTRACTION_COMPONENT,
  ADD_DATA_EXTRACTION_COMPONENT,
  REMOVE_DATA_EXTRACTION_COMPONENT,
} from "@/graphql";

export const firstSaleApi = createApi({
  reducerPath: "api/firstSale",
  tagTypes: [
    "FirstSaleSuppliers",
    "FirstSaleCases",
    "FirstSaleDocuments",
    "FirstSaleValidation",
    "FirstSaleActivity",
    "FirstSaleGeneratedDocs",
    "FirstSaleDocRequests",
    "FirstSaleDataExtraction",
  ],
  baseQuery,
  endpoints: (builder) => ({
    // ─── Queries ──────────────────────────────────────────

    getFirstSaleSuppliers: builder.query<
      any[],
      { tenantId?: string; clientId?: string; status?: string; search?: string }
    >({
      query: (variables) => ({
        document: GET_FIRST_SALE_SUPPLIERS,
        variables,
      }),
      transformResponse: (response: any) => response.firstSaleSuppliers,
      providesTags: ["FirstSaleSuppliers"],
    }),

    getFirstSaleSupplier: builder.query<
      any,
      { supplierId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: GET_FIRST_SALE_SUPPLIER,
        variables,
      }),
      transformResponse: (response: any) => response.firstSaleSupplier,
      providesTags: ["FirstSaleSuppliers"],
    }),

    getFirstSaleCases: builder.query<
      any[],
      { tenantId?: string; clientId?: string; supplierId?: string; status?: string }
    >({
      query: (variables) => ({
        document: GET_FIRST_SALE_CASES,
        variables,
      }),
      transformResponse: (response: any) => response.firstSaleCases,
      providesTags: ["FirstSaleCases"],
    }),

    getFirstSaleCase: builder.query<
      any,
      { caseId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: GET_FIRST_SALE_CASE,
        variables,
      }),
      transformResponse: (response: any) => response.firstSaleCase,
      providesTags: ["FirstSaleCases"],
    }),

    getFirstSaleDocuments: builder.query<
      any[],
      { tenantId?: string; caseId?: string; supplierId?: string; category?: string }
    >({
      query: (variables) => ({
        document: GET_FIRST_SALE_DOCUMENTS,
        variables,
      }),
      transformResponse: (response: any) => response.firstSaleDocuments,
      providesTags: ["FirstSaleDocuments"],
    }),

    getFirstSaleDocumentUrl: builder.query<
      any,
      { docId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: GET_FIRST_SALE_DOCUMENT_URL,
        variables,
      }),
      transformResponse: (response: any) => response.firstSaleDocumentUrl,
    }),

    getFirstSaleValidation: builder.query<
      any,
      { caseId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: GET_FIRST_SALE_VALIDATION,
        variables,
      }),
      transformResponse: (response: any) => response.firstSaleValidation,
      providesTags: ["FirstSaleValidation"],
    }),

    getFirstSaleDocumentSections: builder.query<
      any[],
      {
        multipleMiddlemen?: boolean;
        backToBackFOB?: boolean;
        materialsProvided?: boolean;
        relatedParties?: boolean;
        factorySellsToUnrelated?: boolean;
        middlemanBuysFromUnrelated?: boolean;
      }
    >({
      query: (variables) => ({
        document: GET_FIRST_SALE_DOCUMENT_SECTIONS,
        variables,
      }),
      transformResponse: (response: any) => response.firstSaleDocumentSections,
    }),

    // ─── Mutations ────────────────────────────────────────

    createFirstSaleSupplier: builder.mutation<any, { input: any }>({
      query: ({ input }) => ({
        document: CREATE_FIRST_SALE_SUPPLIER,
        variables: { input },
      }),
      transformResponse: (response: any) => response.createFirstSaleSupplier,
      invalidatesTags: ["FirstSaleSuppliers"],
    }),

    updateFirstSaleSupplier: builder.mutation<
      any,
      { supplierId: string; tenantId?: string; input: any }
    >({
      query: (variables) => ({
        document: UPDATE_FIRST_SALE_SUPPLIER,
        variables,
      }),
      transformResponse: (response: any) => response.updateFirstSaleSupplier,
      invalidatesTags: ["FirstSaleSuppliers"],
    }),

    createFirstSaleCase: builder.mutation<any, { input: any }>({
      query: ({ input }) => ({
        document: CREATE_FIRST_SALE_CASE,
        variables: { input },
      }),
      transformResponse: (response: any) => response.createFirstSaleCase,
      invalidatesTags: ["FirstSaleCases"],
    }),

    updateFirstSaleCase: builder.mutation<
      any,
      { caseId: string; tenantId?: string; input: any }
    >({
      query: (variables) => ({
        document: UPDATE_FIRST_SALE_CASE,
        variables,
      }),
      transformResponse: (response: any) => response.updateFirstSaleCase,
      invalidatesTags: ["FirstSaleCases"],
    }),

    uploadFirstSaleDocument: builder.mutation<any, { input: any }>({
      query: ({ input }) => ({
        document: UPLOAD_FIRST_SALE_DOCUMENT,
        variables: { input },
      }),
      transformResponse: (response: any) => response.uploadFirstSaleDocument,
      invalidatesTags: ["FirstSaleDocuments"],
    }),

    deleteFirstSaleDocument: builder.mutation<
      boolean,
      { docId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: DELETE_FIRST_SALE_DOCUMENT,
        variables,
      }),
      transformResponse: (response: any) => response.deleteFirstSaleDocument,
      invalidatesTags: ["FirstSaleDocuments"],
    }),

    deleteFirstSaleCase: builder.mutation<
      boolean,
      { caseId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: DELETE_FIRST_SALE_CASE,
        variables,
      }),
      transformResponse: (response: any) => response.deleteFirstSaleCase,
      invalidatesTags: ["FirstSaleCases", "FirstSaleDocuments", "FirstSaleValidation", "FirstSaleActivity"],
    }),

    deleteFirstSaleSupplier: builder.mutation<
      boolean,
      { supplierId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: DELETE_FIRST_SALE_SUPPLIER,
        variables,
      }),
      transformResponse: (response: any) => response.deleteFirstSaleSupplier,
      invalidatesTags: ["FirstSaleSuppliers"],
    }),

    runFirstSaleValidation: builder.mutation<
      any,
      { caseId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: RUN_FIRST_SALE_VALIDATION,
        variables,
      }),
      transformResponse: (response: any) => response.runFirstSaleValidation,
      invalidatesTags: ["FirstSaleValidation"],
    }),

    rerunFirstSaleValidationCheck: builder.mutation<
      any,
      { caseId: string; checkId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: RERUN_FIRST_SALE_VALIDATION_CHECK,
        variables,
      }),
      transformResponse: (response: any) => response.rerunFirstSaleValidationCheck,
      invalidatesTags: ["FirstSaleValidation"],
    }),

    updateValidationCheckReview: builder.mutation<
      any,
      { caseId: string; checkId: string; tenantId?: string; input: any }
    >({
      query: (variables) => ({
        document: UPDATE_VALIDATION_CHECK_REVIEW,
        variables,
      }),
      transformResponse: (response: any) => response.updateValidationCheckReview,
      invalidatesTags: ["FirstSaleValidation"],
    }),

    signOffValidation: builder.mutation<
      any,
      { caseId: string; tenantId?: string; approved: boolean; notes?: string }
    >({
      query: (variables) => ({
        document: SIGN_OFF_VALIDATION,
        variables,
      }),
      transformResponse: (response: any) => response.signOffValidation,
      invalidatesTags: ["FirstSaleValidation"],
    }),

    // ─── Data Extraction ──────────────────────────────────────

    getFirstSaleDataExtraction: builder.query<
      any,
      { caseId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: GET_FIRST_SALE_DATA_EXTRACTION,
        variables,
      }),
      transformResponse: (response: any) => response.firstSaleDataExtraction,
      providesTags: ["FirstSaleDataExtraction"],
    }),

    runFirstSaleDataExtraction: builder.mutation<
      any,
      { caseId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: RUN_FIRST_SALE_DATA_EXTRACTION,
        variables,
      }),
      transformResponse: (response: any) => response.runFirstSaleDataExtraction,
      invalidatesTags: ["FirstSaleDataExtraction"],
    }),

    updateDataExtractionComponent: builder.mutation<
      any,
      { caseId: string; componentId: string; input: Record<string, any>; tenantId?: string }
    >({
      query: (variables) => ({
        document: UPDATE_DATA_EXTRACTION_COMPONENT,
        variables,
      }),
      transformResponse: (response: any) => response.updateDataExtractionComponent,
      invalidatesTags: ["FirstSaleDataExtraction"],
    }),

    addDataExtractionComponent: builder.mutation<
      any,
      { caseId: string; input: Record<string, any>; tenantId?: string }
    >({
      query: (variables) => ({
        document: ADD_DATA_EXTRACTION_COMPONENT,
        variables,
      }),
      transformResponse: (response: any) => response.addDataExtractionComponent,
      invalidatesTags: ["FirstSaleDataExtraction"],
    }),

    removeDataExtractionComponent: builder.mutation<
      any,
      { caseId: string; componentId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: REMOVE_DATA_EXTRACTION_COMPONENT,
        variables,
      }),
      transformResponse: (response: any) => response.removeDataExtractionComponent,
      invalidatesTags: ["FirstSaleDataExtraction"],
    }),

    // ─── Activity Events ──────────────────────────────────────

    getFirstSaleActivityEvents: builder.query<
      any[],
      { caseId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: GET_FIRST_SALE_ACTIVITY_EVENTS,
        variables,
      }),
      transformResponse: (response: any) => response.firstSaleActivityEvents,
      providesTags: ["FirstSaleActivity"],
    }),

    createFirstSaleActivityEvent: builder.mutation<
      any,
      {
        caseId: string;
        tenantId?: string;
        type: string;
        title: string;
        description: string;
      }
    >({
      query: (variables) => ({
        document: CREATE_FIRST_SALE_ACTIVITY_EVENT,
        variables,
      }),
      transformResponse: (response: any) =>
        response.createFirstSaleActivityEvent,
      invalidatesTags: ["FirstSaleActivity"],
    }),

    // ─── Case Questions ──────────────────────────────────

    getFirstSaleCaseQuestions: builder.query<
      any[],
      { caseId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: GET_FIRST_SALE_CASE_QUESTIONS,
        variables,
      }),
      transformResponse: (response: any) => response.firstSaleCaseQuestions,
    }),

    // ─── Memo & Letter Generation ──────────────────────

    generateFirstSaleMemo: builder.mutation<
      any,
      { caseId: string; tenantId?: string; attorney?: string }
    >({
      query: (variables) => ({
        document: GENERATE_FIRST_SALE_MEMO,
        variables,
      }),
      transformResponse: (response: any) => response.generateFirstSaleMemo,
      invalidatesTags: ["FirstSaleGeneratedDocs"],
    }),

    generateFirstSaleNotificationLetter: builder.mutation<
      any,
      {
        caseId: string;
        tenantId?: string;
        attorney?: string;
        recipientName?: string;
        recipientTitle?: string;
        importerAddress?: string;
        iorNumber?: string;
      }
    >({
      query: (variables) => ({
        document: GENERATE_FIRST_SALE_NOTIFICATION_LETTER,
        variables,
      }),
      transformResponse: (response: any) =>
        response.generateFirstSaleNotificationLetter,
      invalidatesTags: ["FirstSaleGeneratedDocs"],
    }),

    // ─── Generated Docs CRUD ────────────────────────────

    getFirstSaleGeneratedDocs: builder.query<
      any[],
      { caseId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: GET_FIRST_SALE_GENERATED_DOCS,
        variables,
      }),
      transformResponse: (response: any) => response.firstSaleGeneratedDocs,
      providesTags: ["FirstSaleGeneratedDocs"],
    }),

    saveFirstSaleGeneratedDoc: builder.mutation<
      any,
      { caseId: string; tenantId?: string; type: string; title: string; content: string }
    >({
      query: (variables) => ({
        document: SAVE_FIRST_SALE_GENERATED_DOC,
        variables,
      }),
      transformResponse: (response: any) => response.saveFirstSaleGeneratedDoc,
      invalidatesTags: ["FirstSaleGeneratedDocs"],
    }),

    updateFirstSaleGeneratedDoc: builder.mutation<
      any,
      { docId: string; tenantId?: string; title?: string; content?: string }
    >({
      query: (variables) => ({
        document: UPDATE_FIRST_SALE_GENERATED_DOC,
        variables,
      }),
      transformResponse: (response: any) =>
        response.updateFirstSaleGeneratedDoc,
      invalidatesTags: ["FirstSaleGeneratedDocs"],
    }),

    deleteFirstSaleGeneratedDoc: builder.mutation<
      boolean,
      { docId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: DELETE_FIRST_SALE_GENERATED_DOC,
        variables,
      }),
      transformResponse: (response: any) =>
        response.deleteFirstSaleGeneratedDoc,
      invalidatesTags: ["FirstSaleGeneratedDocs"],
    }),

    // ─── Document Requests ──────────────────────────────

    getFirstSaleDocumentRequests: builder.query<
      any[],
      { caseId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: GET_FIRST_SALE_DOCUMENT_REQUESTS,
        variables,
      }),
      transformResponse: (response: any) => response.firstSaleDocumentRequests,
      providesTags: ["FirstSaleDocRequests"],
    }),

    createFirstSaleDocumentRequests: builder.mutation<
      any[],
      {
        caseId: string;
        tenantId?: string;
        requests: Array<{
          slotId?: string;
          label: string;
          description?: string;
          reason: string;
          requirements?: string[];
          linkedIssues?: Array<{ itemId: string; status: string; detail?: string }>;
        }>;
        notifyEmail?: string;
      }
    >({
      query: (variables) => ({
        document: CREATE_FIRST_SALE_DOCUMENT_REQUESTS,
        variables,
      }),
      transformResponse: (response: any) => response.createFirstSaleDocumentRequests,
      invalidatesTags: ["FirstSaleDocRequests"],
    }),

    fulfillFirstSaleDocumentRequest: builder.mutation<
      any,
      { requestId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: FULFILL_FIRST_SALE_DOCUMENT_REQUEST,
        variables,
      }),
      transformResponse: (response: any) => response.fulfillFirstSaleDocumentRequest,
      invalidatesTags: ["FirstSaleDocRequests"],
    }),

    updateFirstSaleDocumentRequest: builder.mutation<
      any,
      {
        requestId: string;
        tenantId?: string;
        input: {
          reason?: string;
          requirements?: string[];
          linkedIssues?: Array<{ itemId: string; status: string; detail?: string }>;
        };
      }
    >({
      query: (variables) => ({
        document: UPDATE_FIRST_SALE_DOCUMENT_REQUEST,
        variables,
      }),
      transformResponse: (response: any) => response.updateFirstSaleDocumentRequest,
      invalidatesTags: ["FirstSaleDocRequests"],
    }),

    deleteFirstSaleDocumentRequest: builder.mutation<
      boolean,
      { requestId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: DELETE_FIRST_SALE_DOCUMENT_REQUEST,
        variables,
      }),
      transformResponse: (response: any) => response.deleteFirstSaleDocumentRequest,
      invalidatesTags: ["FirstSaleDocRequests"],
    }),

    confirmFirstSaleDocumentUpload: builder.mutation<
      any,
      { docId: string; tenantId?: string }
    >({
      query: (variables) => ({
        document: CONFIRM_FIRST_SALE_DOCUMENT_UPLOAD,
        variables,
      }),
      transformResponse: (response: any) => response.confirmFirstSaleDocumentUpload,
      invalidatesTags: ["FirstSaleDocuments"],
    }),
  }),
});

export const {
  useGetFirstSaleSuppliersQuery,
  useGetFirstSaleSupplierQuery,
  useGetFirstSaleCasesQuery,
  useGetFirstSaleCaseQuery,
  useGetFirstSaleDocumentsQuery,
  useGetFirstSaleDocumentUrlQuery,
  useLazyGetFirstSaleDocumentUrlQuery,
  useGetFirstSaleValidationQuery,
  useGetFirstSaleDocumentSectionsQuery,
  useCreateFirstSaleSupplierMutation,
  useUpdateFirstSaleSupplierMutation,
  useCreateFirstSaleCaseMutation,
  useUpdateFirstSaleCaseMutation,
  useUploadFirstSaleDocumentMutation,
  useDeleteFirstSaleDocumentMutation,
  useDeleteFirstSaleCaseMutation,
  useDeleteFirstSaleSupplierMutation,
  useRunFirstSaleValidationMutation,
  useRerunFirstSaleValidationCheckMutation,
  useUpdateValidationCheckReviewMutation,
  useSignOffValidationMutation,
  useGetFirstSaleActivityEventsQuery,
  useCreateFirstSaleActivityEventMutation,
  useGetFirstSaleCaseQuestionsQuery,
  useGenerateFirstSaleMemoMutation,
  useGenerateFirstSaleNotificationLetterMutation,
  useGetFirstSaleGeneratedDocsQuery,
  useSaveFirstSaleGeneratedDocMutation,
  useUpdateFirstSaleGeneratedDocMutation,
  useDeleteFirstSaleGeneratedDocMutation,
  useGetFirstSaleDocumentRequestsQuery,
  useCreateFirstSaleDocumentRequestsMutation,
  useFulfillFirstSaleDocumentRequestMutation,
  useUpdateFirstSaleDocumentRequestMutation,
  useDeleteFirstSaleDocumentRequestMutation,
  useConfirmFirstSaleDocumentUploadMutation,
  useGetFirstSaleDataExtractionQuery,
  useRunFirstSaleDataExtractionMutation,
  useUpdateDataExtractionComponentMutation,
  useAddDataExtractionComponentMutation,
  useRemoveDataExtractionComponentMutation,
} = firstSaleApi;
